import * as BECaller from '../../util/apiCaller';
import storageHelper from '../../util/storageHelper';

import { setError } from '../../modules/App/ErrorsActions';
import { calculateAvgFe } from '../../modules/FlowEfficiency/FlowEfficiencyActions';
import CalculationsWorker from '../../services/worker';
import { calculateCycleTimeDistribution } from '../../util/calcUtils';

let processService = null;
let processWorker = null;
let lastWorkerParams = null;

const METRICS = {};
function metric(name) {
  if (METRICS[name]) {
    const value = Math.floor((+new Date() - METRICS[name]) / 1000);
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    METRICS[name] = `${minutes} min. ${seconds} sec`;
  } else {
    METRICS[name] = +new Date();
  }
}

function cleanMetric() {
  Object.keys(METRICS).forEach(key => delete METRICS[key]);
}

export const FI_DATA = 'FLOW_INTELLIGENCE_DATA';
export const FI_DATA_LOADING = 'FLOW_INTELLIGENCE_DATA_LOADING';
export const FI_AVG_FE = 'FLOW_INTELLIGENCE_AVG_FE';
export const FI_STATS = 'FLOW_INTELLIGENCE_STATS';

export const INSIGHTS_ERROR = 'INSIGHTS_ERROR';
export const INSIGHTS_LOADING = 'INSIGHTS_LOADING';
export const INSIGHTS_SUCCESS = 'INSIGHTS_SUCCESS';
export const INSIGHTS_STREAMING = 'INSIGHTS_STREAMING';
export const INSIGHTS_PROGRESS = 'INSIGHTS_PROGRESS';
export const INSIGHTS_CLEAN = 'INSIGHTS_CLEAN';

export const INSIGHTS_CARD_TOGGLE = 'INSIGHTS_CARD_TOGGLE';
export const INSIGHTS_CARDS_RESTORE = 'INSIGHTS_CARDS_RESTORE';

export function handleError(error, opt) {
  return setError(error, opt);
}

export function dataLoading() {
  return {
    type: FI_DATA_LOADING,
  };
}

export function insightsError(error, retry) {
  return {
    type: INSIGHTS_ERROR,
    error,
    retry
  };
}

export function insightsLoading(bool) {
  return {
    type: INSIGHTS_LOADING,
    isLoading: bool
  };
}

export function insightsStreaming(bool) {
  return {
    type: INSIGHTS_STREAMING,
    isStreaming: bool
  };
}

export function insightsProgress(message) {
  return {
    type: INSIGHTS_PROGRESS,
    progress: message
  };
}

export function successData(chart, data) {
  return {
    type: FI_DATA,
    chart,
    data
  };
}

export function successInsights(data) {
  return {
    type: INSIGHTS_SUCCESS,
    data
  };
}

export function cleanInsights() {
  return {
    type: INSIGHTS_CLEAN,
  };
}

export function toggleInsightCard(id) {
  return {
    type: INSIGHTS_CARD_TOGGLE,
    id
  }
}

export function restoreCardsHistory(cards) {
  return {
    type: INSIGHTS_CARDS_RESTORE,
    cards
  }
}

function stats(data) {
  return {
    type: FI_STATS,
    data
  }
}

export function getData() {
  return async (dispatch, getState) => {
    cleanMetric();
    metric('FLOW_INTELLIGENCE');

    dispatch(dataLoading());

    const { selected } = getState().dashboards;
    const { data } = getState().app; 
    const { user } = getState().auth;

    const allFilters = getState().filters;
    const {
      allCustomFields,
      excludeWeekends,
      workStart,
      workEnd,
      holidays,
      dates,
      cta
    } = allFilters;
    const filters = allFilters.overview;

    const selectedLists = _(allFilters.allLists)
      .map(l => l.value)
      .filter(l => {
        if (user.config && user.config.enableDoneFilters) {
          return _.indexOf(selected.todo, l._id) === -1
        } else {
          return _.indexOf(selected.todo, l._id) === -1 &&  _.indexOf(selected.done, l._id) === -1;
        }
      })
      .value();

    metric('INIT WORKER');
    const service = new CalculationsWorker();
    let worker = service.wrap();
    worker = await new worker(selected, data, false, {
      enableDoneFilters: user.config.enableDoneFilters,
      enableUnmappedListsActivities: user.config.enableUnmappedListsActivities
    });
    metric('INIT WORKER');

    const getCfd = () => {
      metric('CFD');
      return new Promise(async resolve => {
        try {
          const cfdData = await worker.CFD({
            from: dates.startDate.toISOString(),
            to: dates.endDate.toISOString(),
            lists: selectedLists,
            skipCardDetails: true
          });

          const stats = await worker.CalculateCfdStats({
            lists: selectedLists,
            excludeWeekendOpts: {
              type: excludeWeekends,
              workStart,
              workEnd,
              holidays
            },
            from: dates.startDate.toISOString(),
            to: dates.endDate.toISOString(),
            data: cfdData,
            useWorkingHoursInCycleTimeDays: user.config && user.config.useWorkingHoursInCycleTimeDays
          });
          dispatch(successData('cfd', stats));
          metric('CFD');
          return resolve();
        } catch (err) {
          dispatch(setError(err));
        }
      });
    };

    const getCth = () => {
      metric('CTH');
      return new Promise(async resolve => {
        try {
          const excludeWeekendOpts = {
            type: excludeWeekends,
            workStart,
            workEnd,
            holidays
          };
          let cthData = await worker.CycleTimeHistogram({
            lists: selectedLists,
            excludeWeekendOpts,
            from: dates.startDate.toISOString(),
            to: dates.endDate.toISOString(),
            timeUnit: 'day',
            blockedTime: 'all',
            percentiles: allFilters.allPercentileValues,
            useWorkingHoursInCycleTimeDays: user.config && user.config.useWorkingHoursInCycleTimeDays
          });
          cthData.isFatTailed = calculateCycleTimeDistribution(cthData, excludeWeekendOpts, user.config && user.config.useWorkingHoursInCycleTimeDays);
          dispatch(successData('cth', cthData));
          metric('CTH');
          return resolve();
        } catch (err) {
          dispatch(setError(err));
        }
      });
    };

    const getCta = () => {
      metric('CTA');
      return new Promise(async resolve => {
        try {
          const ctaData = await worker.CycleTimeAging({
            lists: selectedLists,
            excludeWeekendOpts: {
              type: excludeWeekends,
              workStart,
              workEnd,
              holidays
            },
            startDate: dates.startDate.toISOString(),
            endDate: dates.endDate.toISOString(),
            date: dates.singleDate.toISOString(),
            timeUnit: 'day',
            color: true,
            blockedTime: 'all',
            percentiles: undefined,
            healthZones: cta.selectedHealthZones,
            getSingle: true,
            useWorkingHoursInCycleTimeDays: user.config && user.config.useWorkingHoursInCycleTimeDays
          });

          for await (const point of ctaData) {
            dispatch(successData('cta', point));
          }
          metric('CTA');
          return resolve();
        } catch (err) {
          dispatch(setError(err));
        }
      });
    };

    const getAvgFe = () => {
      metric('AVGFE');
      return new Promise(async resolve => {
        try {
          let ctspData = await worker.CycleTimeScatterPlot({
            lists: selectedLists,
            from: dates.startDate.toISOString(),
            to: dates.endDate.toISOString(),
            blockedTime: 'all',
            excludeWeekendOpts: {
              type: excludeWeekends,
              workStart,
              workEnd,
              holidays
            },
            percentiles: allFilters.allPercentileValues,
            useWorkingHoursInCycleTimeDays: user.config && user.config.useWorkingHoursInCycleTimeDays
          });
          dispatch(calculateAvgFe(ctspData.series, FI_AVG_FE));
          metric('AVGFE');
          return resolve();
        } catch (err) {
          dispatch(setError(err));
        }
      });
    }

    let promises = [
      getCfd(),
      getCth(),
      getCta(),
      getAvgFe()
    ];

    await Promise.all(promises);
    metric('FLOW_INTELLIGENCE');
    dispatch(stats(METRICS));
  };
}

export function getInsightsSync(dashboardId, dataHash, from, to, filters) {
  return async (dispatch, getState) => {
    dispatch(cleanInsights());
    dispatch(insightsLoading(true));
    dispatch(insightsProgress('Initializing insights analysis...'));

    return BECaller.api(
      `dashboards/${dashboardId}/insights`,
      'post',
      { dataHash, from, to, filters },
    ).then(data => {
      if (data) {
        switch (data.type) {
          case 'started': 
            dispatch(insightsProgress(data.message || 'Analyzing started...'));
            dispatch(insightsError(data.message, true));
            dispatch(insightsStreaming(false));
            dispatch(insightsLoading(false));
            break;
          case 'thinking':
            dispatch(insightsProgress(data.message || 'Analyzing data...'));
            dispatch(insightsError(data.message, true));
            dispatch(insightsStreaming(false));
            dispatch(insightsLoading(false));
            break;
          case 'waiting':
          case 'error':
            if (!data.waiting) {
              dispatch(handleError(new Error(data.message)));
            }
            dispatch(insightsError(data.message, !!data.retry));
            dispatch(insightsStreaming(false));
            dispatch(insightsLoading(false));
            dispatch(insightsProgress('Error occurred'));
            break;
          default:
            dispatch(successInsights(data));
            dispatch(insightsProgress('Insights generated successfully'));
            dispatch(insightsStreaming(false));
            dispatch(insightsLoading(false));           
        }
      } else {
        dispatch(handleError('No data'));
        dispatch(insightsStreaming(false));
        dispatch(insightsLoading(false));
        dispatch(insightsError('No data', false));
      }
    }).catch(err => {
      dispatch(handleError(err));
      dispatch(insightsStreaming(false));
      dispatch(insightsLoading(false));
      dispatch(insightsProgress('Error occurred'));
    });
  }
}

export function cleanUpInsights() {
  return (dispatch) => {
    dispatch(cleanInsights());
  }
}

export function loadCardsHistory() {
  return (dispatch) => {
    try {
      const history = storageHelper.storage().getItem('insights-cards');
      if (history) {
        const cards = JSON.parse(history);
        if (cards) {
          dispatch(restoreCardsHistory(cards));
        }
      }
    } catch (err) {}
  }
}

export function toggleCard(id) {
  return (dispatch, getState) => {
    dispatch(toggleInsightCard(id));
    try {
      storageHelper.storage().setItem('insights-cards', JSON.stringify(getState().insights.cards))
    } catch (err) {}
  };
}

